<template>
    <div class="mt-12 pb-80">
        <div class="flex mt-[42px] mb-[42px] justify-between h-[42px]">
            <h1
                class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
            >
                {{ $t("vue.my_accounts") }}
            </h1>

            <AtomicButton
                color="yellow"
                size="lg"
                @click="checkAccessOrShowModal() && (createTeamModal = true)"
            >
                <template #icon>
                    <PlusCircleIcon class="mr-2 h-6 w-6" />
                </template>
                {{ $t("vue.create_new_account") }}
            </AtomicButton>
        </div>

        <DynamicContentTable
            class="mt-[44px]"
            :title="$t('vue.your_accounts_overview')"
            :subtitle="$t('vue.list_of_accounts_you_have_access_to')"
            :search-enabled="false"
            :rows="teamsRows"
            :columns="teamColumns"
            :endpoint="undefined"
            :base-query="{}"
            :table="singlePageTable(teamLength)"
            :pagination="singlePagePagination(teamLength)"
            :pagination-enabled="false"
            :per-page-options="[]"
            per-page-label=""
            :row-key="(r: any) => r.id"
        >
            <template #row="{ row: team }">
                <td class="px-[21px] py-[14px] !cursor-default">
                    <div class="flex items-center gap-[7px]">
                        <div
                            class="h-[22px] w-[22px] rounded-full ring-[1px] ring-gray-200 overflow-hidden bg-gray-100 shrink-0"
                        >
                            <img
                                v-if="team.profile_photo_url"
                                :src="team.profile_photo_url"
                                class="h-[22px] w-[22px] object-cover"
                                alt="Team photo"
                            />
                        </div>

                        <span class="text-[14px] font-normal text-[#4B5563]">
                            {{ team.name }}
                        </span>
                    </div>
                </td>

                <td class="px-[21px] py-[14px] !cursor-default">
                    <span class="text-[13px] font-medium text-[#4B5563]">
                        {{ team.id }}
                    </span>
                </td>

                <td class="px-[21px] py-[14px] !cursor-default">
                    <span class="text-[13px] font-medium text-[#4B5563]">
                        {{ team.domains_quantity }}
                    </span>
                </td>

                <td class="px-[21px] py-[14px] !cursor-default">
                    <span
                        v-if="team.paddle_sub_amount > 0"
                        class="text-[13px] font-medium text-[#4B5563]"
                    >
                        {{ team.paddle_sub_amount }} $
                    </span>
                    <span v-else class="text-[13px] font-medium text-[#4B5563]">
                        {{ $t("vue.free") }}
                    </span>
                </td>

                <td class="px-[21px] py-[14px] !cursor-default">
                    <span class="text-[13px] font-medium text-[#4B5563]">
                        {{ team.subscription_level }}
                    </span>
                </td>

                <td class="px-[21px] py-[14px] !cursor-default">
                    <span class="text-[13px] font-medium text-[#4B5563]">
                        {{ format(new Date(team.created_at), "d. L. yyyy") }}
                    </span>
                </td>

                <td class="px-[21px] py-[14px] !cursor-default">
                    <div
                        v-if="team.isInvitation"
                        class="flex items-center justify-center gap-3"
                    >
                        <button
                            type="button"
                            :disabled="loading"
                            class="inline-flex h-[28px] items-center justify-center rounded-[6px] bg-[#22C55E] px-[14px] text-[14px] font-medium leading-none text-white disabled:opacity-60 cursor-pointer group-hover:text-gray-600 group-focus:text-gray-600 group-disabled:text-white"
                            @click.stop="
                                acceptInvitation(team.invitation_token)
                            "
                        >
                            {{ $t("vue.accept") }}
                        </button>

                        <button
                            type="button"
                            :disabled="loading"
                            class="inline-flex h-[28px] items-center justify-center rounded-[6px] bg-[#EF4444] px-[14px] text-[14px] font-medium leading-none text-white disabled:opacity-60 cursor-pointer group-hover:text-gray-600 group-focus:text-gray-600 group-disabled:text-white"
                            @click.stop="
                                refuseInvitation(team.invitation_token)
                            "
                        >
                            {{ $t("vue.decline") }}
                        </button>
                    </div>

                    <div v-else class="flex items-center justify-center">
                        <span
                            v-if="teamProps.id === team.id"
                            class="text-[13px] font-medium text-[#4B5563]"
                        >
                            {{ $t("vue.current") }}
                        </span>

                        <AtomicButton
                            v-else
                            color="white"
                            class="!inline-flex !h-[28px] !rounded-[6px] !border !border-[#088BEA] !bg-white !px-[14px] !py-0 !text-[14px] !font-medium !leading-none !text-[#088BEA]"
                            @click.stop="switchTeam(team.id)"
                        >
                            {{ $t("vue.switch_account") }}
                        </AtomicButton>
                    </div>
                </td>
            </template>
        </DynamicContentTable>
    </div>

    <CreateTeamModal v-if="createTeamModal" @close="createTeamModal = false" />
    <UpgradeRequiredModal
        :opened="showUpgradeModal"
        title="Scale Plan Required"
        description="Creating new accounts requires a Scale plan subscription. Upgrade your subscription to access team management features."
        @close="closeUpgradeModal"
    />
</template>

<script lang="ts" setup>
import { format } from "date-fns";
import { PlusCircleIcon } from "@heroicons/vue/24/outline";
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import { computed, ref } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";
import useTeamMemberAccess from "@/composables/useTeamMemberAccess";
import UpgradeRequiredModal from "@/components/Atomic/Modals/UpgradeRequiredModal.vue";
import type IInvitation from "@/types/Members/IInvitation.ts";

const page = usePage<IRhizomPageProps>();

const props = defineProps<{
    teams: IPaginationData<{
        id: number;
        name: string;
        profile_photo_url: string;
        personal_team: boolean;
        created_at: string;
        isInvitation?: boolean;
        invitation_token?: string;
        domains_quantity?: number;
        subscription_level?: string;
        paddle_sub_amount?: number;
    }>;
}>();

const { t } = useTranslations();
const { showUpgradeModal, checkAccessOrShowModal, closeUpgradeModal } =
    useTeamMemberAccess();
const createTeamModal = ref(false);

const globalProps = computed(() => usePage<IRhizomPageProps>().props);
const teamProps = computed(() => globalProps.value.team as ICurrentTeam);

const form = ref({
    id: {} as number,
});

const switchTeam = (teamId: number) => {
    form.value.id = teamId;
    router.post(route("teams.switch.store"), form.value, {});
};

const teamsRows = computed(() => props.teams.data);
const teamLength = computed(() => teamsRows.value.length);

const teamColumns = computed(() => [
    {
        key: "account",
        label: t("vue.account_name") ?? "Account Name",
        colClass: "w-[42%]",
        sortable: false,
    },
    {
        key: "id",
        label: t("vue.account_id") ?? "Account ID",
        colClass: "w-[14%]",
        sortable: false,
    },
    {
        key: "domains_quantity",
        label: t("vue.domains_count") ?? "Domains Quantity",
        colClass: "w-[14%]",
        sortable: false,
    },
    {
        key: "subscription_level",
        label:
            t("vue.paddle_sub_last_transaction_amount_short") ?? "Payed amount",
        colClass: "w-[14%]",
        sortable: false,
    },
    {
        key: "paddle_sub_amount",
        label: t("vue.subscription_level") ?? "Subscription Level",
        colClass: "w-[14%]",
        sortable: false,
    },
    {
        key: "created_at",
        label: t("vue.created_at"),
        colClass: "w-[14%]",
        sortable: false,
    },
    {
        key: "actions",
        label: "",
        colClass: "w-[30%]",
        sortable: false,
    },
]);

type SortDir = "asc" | "desc";

const singlePageTable = (total: number) => ({
    sort_by: null,
    sort_dir: null as SortDir | null,
    per_page: total || 1,
    current_page: 1,
    q: "",
});

const singlePagePagination = (total: number) => ({
    total,
    per_page: total || 1,
    current_page: 1,
    last_page: 1,
    from: total ? 1 : null,
    to: total ? total : null,
});

const loading = ref(false);

const acceptInvitation = async (invitationToken: string) => {
    router.get(
        route("team.invitation.accept", { token: invitationToken }),
        {},
        {
            preserveScroll: true,
            preserveState: false,
            onFinish: () => (loading.value = false),
        },
    );
};

const refuseInvitation = async (invitationToken: string) => {
    router.get(
        route("team.invitation.refuse", { token: invitationToken }),
        {},
        {
            preserveScroll: true,
            preserveState: false,
            onFinish: () => (loading.value = false),
        },
    );
};
</script>

<style scoped></style>
